import { niceScale, padRange, pctTick, signed, shortDate, useBoxWidth } from "./srUtil";

const N = 126, RING = 2 * Math.PI * 91;

const Dial = ({ age }) => (
  <svg viewBox="0 0 260 245" role="img" aria-label="Trading days since the buying became public" data-testid="sr-clock-dial">
    <circle cx="130" cy="113" r="91" fill="none" stroke="#26303b" strokeWidth="12" />
    <circle cx="130" cy="113" r="91" fill="none" stroke="#38bdf8" strokeWidth="12" strokeLinecap="round"
      strokeDasharray={`${(RING * Math.min(age, N)) / N} ${RING}`} transform="rotate(-90 130 113)" />
    <text x="130" y="84" textAnchor="middle" fill="#a0aab8" fontSize="12" letterSpacing="3">DAY</text>
    <text x="130" y="130" textAnchor="middle" fill="#e6eaf0" fontSize="48" fontWeight="600" data-testid="sr-clock-day">{age}</text>
  </svg>
);

const ClockChart = ({ s }) => {
  const [ref, w] = useBoxWidth(900);
  const W = Math.max(300, w), narrow = W < 600, H = narrow ? 300 : 380, X0 = narrow ? 40 : 52, X1 = W - (narrow ? 14 : 33), bot = H - 55;
  const path = s.path.slice(0, N + 1), t = s.typical, age = path.length - 1;
  const vals = [...path, ...(t ? [...t.p25, ...t.p75] : [])];
  const sc = niceScale(...padRange(Math.min(0, ...vals), Math.max(0, ...vals)), 5);
  const x = (i) => X0 + (i / N) * (X1 - X0), y = (v) => bot - ((v - sc.min) / (sc.max - sc.min)) * (bot - 35);
  const line = (a) => a.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const band = t && line(t.p75) + " " + t.p25.map((_, k) => { const i = t.p25.length - 1 - k; return `L${x(i).toFixed(1)},${y(t.p25[i]).toFixed(1)}`; }).join(" ") + " Z";
  return (
    <div ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" data-testid="sr-clock-chart"
        aria-label="The stock's gain against its sector since the buying became public, next to how similar stocks moved">
        {sc.ticks.map((v) => (
          <g key={v}><line x1={X0} x2={X1} y1={y(v)} y2={y(v)} stroke={v === 0 ? "#5b6675" : "#26303b"} strokeDasharray={v === 0 ? "0" : "2 5"} />
            <text x={X0 - 8} y={y(v) + 4} textAnchor="end" fill="#8e9aa9" fontSize="11">{pctTick(v)}</text></g>
        ))}
        {t && <path d={band} fill="#a0aab8" opacity=".12" />}
        {t && <path d={line(t.p50)} stroke="#8e9aa9" strokeWidth="1.8" strokeDasharray="5 5" fill="none" />}
        <path d={line(path)} stroke="#38bdf8" strokeWidth="3" fill="none" />
        <line x1={x(age)} x2={x(age)} y1="30" y2={bot} stroke="#38bdf8" strokeDasharray="4 5" />
        <circle cx={x(age)} cy={y(path[age])} r="4" fill="#38bdf8" />
        <text x={x(age)} y="20" textAnchor="middle" fill="#38bdf8" fontSize="12">today</text>
        {[0, 21, 42, 63, 84, 105, 126].map((v) => <text key={v} x={x(v)} y={bot + 23} textAnchor="middle" fill="#8e9aa9" fontSize="11">{v}</text>)}
        <text x={(X0 + X1) / 2} y={H - 6} textAnchor="middle" fill="#8e9aa9" fontSize="10">
          {narrow ? "Trading days since it became public" : "Trading days since the funds’ buying became public"}</text>
      </svg>
    </div>
  );
};

const RegimeBox = ({ r }) => (
  <div><span className="eyebrow">Regime nowcast</span>
    {r ? (
      <><button className="regimelabel" onClick={() => document.getElementById("sr-pattern")?.scrollIntoView({ behavior: "smooth" })}
        data-testid="sr-regime-expand">{r.name} <span>↓</span></button>
        <small>Closest match · details below</small></>
    ) : <><h3 className="muted" data-testid="sr-regime-expand">Not available</h3><small>No regime reading for this stock yet</small></>}
  </div>
);

export const TradeClock = ({ d }) => {
  const s = d.short;
  const st = s.above_typical == null ? ["No comparison available", "muted"] : s.above_typical ? ["Ahead of similar stocks at this point", "green"] : ["Behind similar stocks at this point", "amber"];
  const tr = s.trend_21;
  const t = s.typical;
  return (
    <section className="card tradeclock fadein" data-testid="sr-trade-clock">
      <div className="clockheading row between"><span className="eyebrow">Performance against the sector since the buying became public</span></div>
      <div className="clocklayout">
        <div className="clockaside"><Dial age={s.day} /><div className={`clockstatus ${st[1]}`} data-testid="sr-clock-status">{st[0]}</div></div>
        <div className="clockplot">
          <ClockChart s={s} />
          <div className="clocklegend"><span className="blue">━━ {d.symbol}</span><span>┄┄ Typical similar stock</span><span>▰ Usual range</span></div>
          <p className="smallnote">{d.symbol}’s gain or loss against similar stocks in its sector since the funds’ buying became public on{" "}
            {shortDate(s.reveal_date, true)}{t ? `, next to how ${t.n} similar past cases moved at the same point` : ""}. Not a forecast; the line ends today.</p>
          <div className="clockcontext">
            <RegimeBox r={s.regime} />
            <div><span className="eyebrow">Last month</span>
              <h3 className={tr == null ? "muted" : tr >= 0 ? "green" : "red"} data-testid="sr-clock-trend">{tr == null ? "—" : `${signed(tr)}%`}</h3>
              <small>Against the {d.sector} sector</small></div>
            <div><span className="eyebrow">How to read</span><p>Compare {d.symbol} with similar stocks at the same point in time. The day count isn’t a countdown to a price target.</p></div>
          </div>
        </div>
      </div>
    </section>
  );
};
